import {
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { disableAnimationsAtom } from "@sunrise/yallo-settings";
import { useAtomValue, useSetAtom } from "jotai";
import {
  type ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  EscapeRouteLayout,
  PageSpinner,
  RouteLayout,
  Spinner,
} from "@/components";
import { globalFocusKey } from "@/config/focus-key";
import {
  GUIDE_WINDOW_DAYS_IN_FUTURE,
  GUIDE_WINDOW_DAYS_IN_PAST,
} from "@/core/constants";
import { GuideControls } from "@/features/guide/guide-controls";
import { GuideGrid } from "@/features/guide/guide-grid";
import { SelectedProgram } from "@/features/guide/selected-program";
import { useMenu } from "@/features/menu/use-menu";
import { OnBlurRequestFunction } from "@/features/settings/types";
import { useAutoSelectProgram } from "@/modules/guide/hooks/use-auto-select-program";
import { useGuideData } from "@/modules/guide/hooks/use-guide-data";
import { selectorGridCoordinates } from "@/modules/guide/store";
import { actionMenuExpand, menuAtom } from "@/modules/menu/menu.store";
import * as styles from "@/routes/guide.css";
import { isArrowLeftKey, isArrowUpKey } from "@/utils/navigation";

const localWidgetFocusKey = {
  grid: globalFocusKey.activePage + ".grid",
  navigation: globalFocusKey.activePage + ".navigation",
};

function ConnectedGuide(): ReactElement {
  useAutoSelectProgram();

  const { data, startTime, endTime, onVisibleDataChanged, initialLoading } =
    useGuideData({
      daysInFuture: GUIDE_WINDOW_DAYS_IN_FUTURE,
      daysInPast: GUIDE_WINDOW_DAYS_IN_PAST,
    });

  const dispatchMenu = useSetAtom(menuAtom);

  const offset = useAtomValue(selectorGridCoordinates);

  const disableAnimationsAtomValue = useAtomValue(disableAnimationsAtom);

  const onBlurRequestGuide: OnBlurRequestFunction = useCallback(
    (direction) => {
      if (isArrowLeftKey(direction)) {
        dispatchMenu(actionMenuExpand());
      }

      if (isArrowUpKey(direction) || direction === "back") {
        setFocus(localWidgetFocusKey.navigation);
      }
    },
    [dispatchMenu],
  );

  return (
    <EscapeRouteLayout className={styles.guideGridContainer}>
      {initialLoading ? (
        <Spinner data-testid="spinner-grid" className={styles.gridLoader} />
      ) : null}
      <GuideGrid
        onBlurRequest={onBlurRequestGuide}
        focusKey={localWidgetFocusKey.grid}
        data={data}
        disableAnimations={disableAnimationsAtomValue}
        endTime={endTime}
        offset={offset}
        startTime={startTime}
        onVisibleDataChanged={onVisibleDataChanged}
      />
    </EscapeRouteLayout>
  );
}

export default function Guide(): ReactElement {
  // Back behaviour is handled elsewhere.
  // But as long as we are loading, we need to handle back behaviour.
  const [guideLoading, setGuideLoading] = useState(false);

  const menu = useMenu({ expandOnBack: guideLoading, shouldShowMenu: true });

  const { focusSelf, ref } = useFocusable({
    focusKey: globalFocusKey.activePage,
    onArrowPress: function handleArrowPress(direction) {
      if (isArrowLeftKey(direction)) menu.expand();
      return false;
    },
  });

  useEffect(
    function delegateFocus() {
      if (menu.isExpanded) return;
      setFocus(localWidgetFocusKey.grid);
    },
    [focusSelf, menu.isExpanded],
  );

  const dispatchMenu = useSetAtom(menuAtom);
  const expandMenu = useCallback(() => {
    dispatchMenu(actionMenuExpand());
  }, [dispatchMenu]);

  return (
    <main ref={ref} tabIndex={0} data-testid="guide" className={styles.root}>
      <Suspense
        fallback={
          <PageSpinner onVisible={setGuideLoading} data-testid="guide" />
        }
      >
        <RouteLayout>
          <GuideControls
            focusKey={localWidgetFocusKey.navigation}
            onBack={expandMenu}
          />
          <Suspense>
            <SelectedProgram
              className={styles.programArea}
              data-testid="guide"
            />
          </Suspense>
          <ConnectedGuide />
        </RouteLayout>
      </Suspense>
    </main>
  );
}
